import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { FaqSection } from '../components/FaqSection'
import { FactoryLogo } from '../components/FactoryLogo'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { useAuth } from '../contexts/AuthContext'
import { usePageMeta } from '../hooks/usePageMeta'
import { useJsonLd } from '../hooks/useJsonLd'
import { pageMeta } from '../seo/pageMeta'
import {
  sendEstimateMessage,
  getEstimateConversation,
  recordEstimateEvent,
  ESTIMATE_REF_KEY,
  EstimateChatSummary,
  EstimateChatMessage,
} from '../api/client'

// Fire-and-forget funnel event; analytics must never break the page.
function trackEstimateEvent(...args: Parameters<typeof recordEstimateEvent>) {
  recordEstimateEvent(...args).catch(() => {})
}

// Worked examples. The chat's empty state offers them as one-click prompts; the
// landing copy below the tool expands each one into what that kind of project
// actually forces an estimate to reason about.
const WORKED_EXAMPLES = [
  {
    prompt: 'A SaaS dashboard that tracks developer productivity across our GitHub repos',
    kind: 'New SaaS build',
    weighs: 'Multi-tenant data model, GitHub API rate limits and backfill, metric definitions, auth and billing, and how much of the UI is genuinely custom.',
  },
  {
    prompt: 'Exit AWS and move our workloads to bare-metal servers we run ourselves',
    kind: 'Cloud exit',
    weighs: 'Which managed services have no self-hosted equal, data egress and cutover windows, the ops load you are taking on, and what has to be rebuilt versus lifted as-is.',
  },
  {
    prompt: 'Migrate a legacy Rails monolith to a modular service architecture',
    kind: 'Migration',
    weighs: 'Where the seams actually are, shared database ownership, the strangler path that keeps the app shippable throughout, and test coverage before you start moving things.',
  },
  {
    prompt: 'A CLI tool that automates database migrations with safe rollbacks',
    kind: 'Developer tool',
    weighs: 'Which engines you support, how rollbacks behave on partially applied migrations, distribution and versioning, and the failure modes worth designing for up front.',
  },
  {
    prompt: 'A mobile app for field technicians that works offline and syncs when back online',
    kind: 'Mobile app',
    weighs: 'Conflict resolution on sync, local storage and schema migration on-device, native versus cross-platform, and app store release mechanics.',
  },
]

const EXAMPLE_PROMPTS = WORKED_EXAMPLES.map((e) => e.prompt)

const ESTIMATE_CONTAINS = [
  { label: 'Complexity score', detail: 'A 1–10 rating with the reasoning behind it, not just a number.' },
  { label: 'Recommended stack', detail: 'Frontend, backend, infrastructure, and a suggested hosting approach.' },
  { label: 'Workstreams', detail: 'The pieces of work involved, each tagged low, medium, or high effort.' },
  { label: 'Key considerations', detail: 'The decisions and risks that will actually move the timeline.' },
  { label: 'Two timelines', detail: 'A solo-developer estimate next to a small-team-with-AI estimate.' },
  { label: 'Suggested approach', detail: 'A short architecture summary describing how to build it.' },
]

const HOW_IT_WORKS = [
  { step: 'Describe it', detail: 'Write what you want to build, migrate, or replace — plain English, no form fields.' },
  { step: 'Answer a few questions', detail: 'The estimator asks about scale, integrations, and constraints where they change the answer.' },
  { step: 'Watch the estimate take shape', detail: 'The card beside the conversation updates as you talk — complexity, stack, workstreams, timelines.' },
  { step: 'Build it or take it away', detail: 'Hand the estimate to the agent swarm, or keep it as scoping input. No email required either way.' },
]

const COMPLEXITY_COLORS: Record<string, string> = {
  Simple: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  Moderate: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  Complex: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  Ambitious: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
}

const EFFORT_COLORS: Record<string, string> = {
  low: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  high: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
}

type ChatMsg = { role: 'user' | 'assistant'; content: string }

export default function EstimatePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const [conversationId, setConversationId] = useState('')
  const [messages, setMessages] = useState<ChatMsg[]>([])
  const [summary, setSummary] = useState<EstimateChatSummary | null>(null)
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [limitReached, setLimitReached] = useState(false)
  const [repliesUsed, setRepliesUsed] = useState<number | null>(null)
  const [repliesLimit, setRepliesLimit] = useState<number | null>(null)

  const threadRef = useRef<HTMLDivElement>(null)

  // Resume an existing conversation when an ID is in the URL.
  useEffect(() => {
    if (!id) return
    getEstimateConversation(id)
      .then((conv) => {
        setConversationId(conv.id)
        setSummary(conv.summary)
        setMessages(
          (conv.messages || []).map((m: EstimateChatMessage) => ({ role: m.role, content: m.content })),
        )
      })
      .catch(() => setError('This estimate could not be loaded.'))
  }, [id])

  // Keep the thread scrolled to the latest message.
  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, sending])

  const baseMeta = pageMeta('/estimate')
  usePageMeta({
    title: summary?.title ? `${summary.title} — Estimate — Factory Nexus` : baseMeta.title,
    description: baseMeta.description,
    path: id ? `/estimate/${id}` : '/estimate',
  })
  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Factory Nexus Idea Estimator',
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Web',
    url: 'https://factory-nexus.ai/estimate',
    description: baseMeta.description,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  })

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (trimmed.length < 2 || sending) return

      setError(null)
      setSending(true)
      setInput('')
      setMessages((prev) => [...prev, { role: 'user', content: trimmed }])

      try {
        const resp = await sendEstimateMessage(conversationId, trimmed)
        setConversationId(resp.conversation_id)
        if (!conversationId) {
          window.history.replaceState(null, '', `/estimate/${resp.conversation_id}`)
        }
        setMessages((prev) => [...prev, { role: 'assistant', content: resp.reply }])
        if (resp.summary) setSummary(resp.summary)
        setRepliesUsed(resp.replies_used)
        setRepliesLimit(resp.replies_limit)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Something went wrong'
        if (msg === 'reply_limit_reached') {
          setLimitReached(true)
          // Drop the optimistic user message we couldn't answer.
          setMessages((prev) => prev.slice(0, -1))
          setInput(trimmed)
        } else {
          setError(msg)
          setMessages((prev) => prev.slice(0, -1))
          setInput(trimmed)
        }
      } finally {
        setSending(false)
      }
    },
    [conversationId, sending],
  )

  const startNew = useCallback(() => {
    if (conversationId) trackEstimateEvent(conversationId, { event_type: 'cta_clicked', cta: 'retry' })
    setConversationId('')
    setMessages([])
    setSummary(null)
    setInput('')
    setError(null)
    setLimitReached(false)
    setRepliesUsed(null)
    setRepliesLimit(null)
    navigate('/estimate', { replace: true })
  }, [conversationId, navigate])

  const unlimited = repliesLimit !== null && repliesLimit < 0
  const showMeter = repliesLimit !== null && repliesLimit >= 0 && repliesUsed !== null

  return (
    <div className="min-h-screen flex flex-col">
      {isAuthenticated ? (
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-gray-900 dark:text-white">
              <FactoryLogo className="w-7 h-7" />
              <span className="font-semibold">Factory Nexus</span>
            </Link>
            <Link to="/dashboard" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600">
              ← Back to Dashboard
            </Link>
          </div>
        </header>
      ) : (
        <PublicNavBar />
      )}

      <div className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-6">
          <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wider">
            Idea Estimator
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-1">
            Free AI Software Project Estimator
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Talk through what you want to build, migrate, or change — and shape the estimate as you go. Free, no
            signup required.
          </p>
          <Link
            to="/estimate/examples"
            className="inline-block mt-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500"
          >
            See what others are building &rarr;
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Conversation */}
          <div className="lg:col-span-3 flex flex-col">
            <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col min-h-[24rem]">
              <div ref={threadRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 max-h-[60vh]">
                {messages.length === 0 && !sending && (
                  <div className="text-center py-8">
                    <p className="text-gray-600 dark:text-gray-300 mb-5">
                      Describe your project, migration, or technical problem. I'll ask a few questions and build a live estimate.
                    </p>
                    <div className="flex flex-col gap-2 max-w-lg mx-auto">
                      {EXAMPLE_PROMPTS.map((ex, i) => (
                        <button
                          key={i}
                          onClick={() => send(ex)}
                          className="text-left px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:border-primary-400 hover:text-primary-600 transition-colors"
                        >
                          {ex}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                        m.role === 'user'
                          ? 'bg-primary-600 text-white rounded-br-sm'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-sm'
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}

                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-bl-sm px-4 py-3">
                      <LoadingSpinner size="xs" />
                    </div>
                  </div>
                )}
              </div>

              {/* Composer / upsell */}
              <div className="border-t border-gray-100 dark:border-gray-700 p-3 sm:p-4">
                {limitReached ? (
                  <UpsellBanner authenticated={isAuthenticated} />
                ) : (
                  <>
                    <div className="flex items-end gap-2">
                      <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Type your message…"
                        rows={2}
                        maxLength={4000}
                        disabled={sending}
                        className="flex-1 resize-none rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:outline-none disabled:opacity-60"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            send(input)
                          }
                        }}
                      />
                      <button
                        onClick={() => send(input)}
                        disabled={sending || input.trim().length < 2}
                        className="px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Send
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-2 min-h-[1.25rem]">
                      <span className="text-xs text-gray-400">
                        {showMeter ? (
                          <>
                            {repliesUsed} of {repliesLimit} responses used
                            {!isAuthenticated && ' · '}
                            {!isAuthenticated && (
                              <Link to="/register" className="text-primary-600 hover:text-primary-500">
                                sign up for more
                              </Link>
                            )}
                          </>
                        ) : unlimited ? (
                          'Unlimited responses'
                        ) : (
                          'Enter to send · Shift+Enter for a new line'
                        )}
                      </span>
                      {messages.length > 0 && (
                        <button onClick={startNew} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                          New estimate
                        </button>
                      )}
                    </div>
                  </>
                )}

                {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
              </div>
            </div>
          </div>

          {/* Live estimate card */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-6">
              {summary ? (
                <SummaryCard summary={summary} estimateId={conversationId} />
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center text-sm text-gray-400">
                  Your estimate will take shape here as we talk.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Landing copy — only on a fresh estimate, so it never sits under a live
          conversation. This is what /estimate serves to crawlers (P1). */}
      {messages.length === 0 && !summary && <EstimatorLanding onExample={send} />}

      {!isAuthenticated && <PublicFooter />}
    </div>
  )
}

function EstimatorLanding({ onExample }: { onExample: (prompt: string) => void }) {
  return (
    <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 pb-8">
      <section className="pt-4 pb-12 border-t border-gray-100 dark:border-gray-800">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6">Each estimate contains</h2>
        <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {ESTIMATE_CONTAINS.map((item) => (
            <div key={item.label}>
              <dt className="font-semibold text-gray-900 dark:text-white">{item.label}</dt>
              <dd className="text-sm text-gray-500 dark:text-gray-400 mt-1">{item.detail}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="py-12 border-t border-gray-100 dark:border-gray-800">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6">How it works</h2>
        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {HOW_IT_WORKS.map((s, i) => (
            <li key={s.step}>
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-sm font-semibold mb-2">
                {i + 1}
              </span>
              <h3 className="font-semibold text-gray-900 dark:text-white">{s.step}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{s.detail}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="py-12 border-t border-gray-100 dark:border-gray-800">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">Example estimates</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Five projects people estimate here, and what each one forces the estimate to reason about. Click any of
          them to run it.
        </p>
        <ul className="space-y-4">
          {WORKED_EXAMPLES.map((ex) => (
            <li
              key={ex.prompt}
              className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">{ex.kind}</p>
              <h3 className="font-semibold text-gray-900 dark:text-white">{ex.prompt}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                <span className="font-medium text-gray-600 dark:text-gray-300">What it weighs: </span>
                {ex.weighs}
              </p>
              <button
                onClick={() => onExample(ex.prompt)}
                className="mt-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500"
              >
                Estimate this &rarr;
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="py-12 border-t border-gray-100 dark:border-gray-800">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4">
          How is this different from other project estimators?
        </h2>
        <div className="space-y-3 text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl">
          <p>
            Most estimator tools are a form and a lead-capture gate: fill in the fields, hand over an email, wait for
            a PDF. This one is a conversation. You describe the project, it asks the questions that actually change
            the answer, and the estimate rebuilds itself in front of you as you go.
          </p>
          <p>
            There is no signup wall, no email required, and nothing to download — you can read your estimate the
            moment it exists. It also covers work that isn&apos;t a greenfield build: migrations, refactors, and
            infrastructure moves like leaving a cloud provider.
          </p>
          <p>
            And when the estimate looks right, you don&apos;t have to take it somewhere else to act on it.{' '}
            <Link to="/" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">
              Parallel AI coding agents
            </Link>{' '}
            can decompose it into tasks and start building on isolated git branches, with{' '}
            <Link to="/products" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">
              AI code review
            </Link>{' '}
            on every branch before anything merges.
          </p>
        </div>
      </section>

      <div className="border-t border-gray-100 dark:border-gray-800">
        <FaqSection path="/estimate" heading="Idea estimator FAQ" />
      </div>
    </div>
  )
}

function UpsellBanner({ authenticated }: { authenticated: boolean }) {
  if (authenticated) {
    return (
      <div className="text-center p-3">
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
          You've used your free responses. Upgrade for unlimited estimating.
        </p>
        <Link
          to="/billing"
          className="inline-block px-5 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-500 transition-colors"
        >
          Upgrade plan
        </Link>
      </div>
    )
  }
  return (
    <div className="text-center p-3">
      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
        You've used your free responses. Sign up free to keep going — 10 responses on the house.
      </p>
      <div className="flex items-center justify-center gap-3">
        <Link
          to="/register"
          className="px-5 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-500 transition-colors"
        >
          Sign up free
        </Link>
        <Link to="/login" className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600">
          Log in
        </Link>
      </div>
    </div>
  )
}

/** The build prompt handed to the builder: the idea plus what the estimate worked out. */
export function buildPromptFromSummary(summary: EstimateChatSummary): string {
  const parts = [summary.title]
  if (summary.architecture_summary) parts.push(summary.architecture_summary)
  const stack = [
    ...(summary.tech_stack?.frontend ?? []),
    ...(summary.tech_stack?.backend ?? []),
    ...(summary.tech_stack?.infrastructure ?? []),
  ]
  if (stack.length) parts.push(`Stack: ${stack.join(', ')}`)
  if (summary.workstreams?.length) {
    parts.push(['Scope:', ...summary.workstreams.map((w) => `- ${w.name}: ${w.description}`)].join('\n'))
  }
  return parts.filter(Boolean).join('\n\n')
}

export function SummaryCard({ summary, estimateId }: { summary: EstimateChatSummary; estimateId?: string }) {
  const { isAuthenticated } = useAuth()
  const buildPrompt = buildPromptFromSummary(summary)
  const ts = summary.tech_stack
  const onBuildClick = () => {
    if (!estimateId) return
    try {
      sessionStorage.setItem(ESTIMATE_REF_KEY, estimateId)
    } catch {
      // storage unavailable — attribution is best-effort
    }
    trackEstimateEvent(estimateId, { event_type: 'cta_clicked', cta: 'build' })
  }
  const hasStack =
    ts && (ts.frontend?.length || ts.backend?.length || ts.infrastructure?.length || ts.suggested_hosting)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {summary.project_type && (
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{summary.project_type}</p>
            )}
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {summary.title || 'Working estimate'}
            </h2>
          </div>
          {summary.complexity && (
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                COMPLEXITY_COLORS[summary.complexity] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {summary.complexity}
            </span>
          )}
        </div>
      </div>

      {/* Complexity */}
      {summary.complexity_score > 0 && (
        <div className="p-5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Complexity</span>
            <span className="text-xl font-bold text-gray-900 dark:text-white">{summary.complexity_score}/10</span>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 mb-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(summary.complexity_score, 10) * 10}%` }}
            />
          </div>
          {summary.complexity_reasoning && (
            <p className="text-sm text-gray-600 dark:text-gray-300">{summary.complexity_reasoning}</p>
          )}
        </div>
      )}

      {/* Key considerations */}
      {summary.key_considerations?.length > 0 && (
        <div className="p-5 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Key Considerations
          </h3>
          <ul className="space-y-1.5">
            {summary.key_considerations.map((k, i) => (
              <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                <span className="text-primary-500 mt-0.5">•</span>
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tech stack */}
      {hasStack && (
        <div className="p-5 border-b border-gray-100 dark:border-gray-700 space-y-3">
          <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stack</h3>
          {ts.frontend?.length > 0 && <StackRow label="Frontend" items={ts.frontend} color="blue" />}
          {ts.backend?.length > 0 && <StackRow label="Backend" items={ts.backend} color="green" />}
          {ts.infrastructure?.length > 0 && <StackRow label="Infrastructure" items={ts.infrastructure} color="purple" />}
          {ts.suggested_hosting && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium">Hosting:</span> {ts.suggested_hosting}
            </p>
          )}
        </div>
      )}

      {/* Workstreams */}
      {summary.workstreams?.length > 0 && (
        <div className="p-5 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Workstreams
          </h3>
          <div className="space-y-2.5">
            {summary.workstreams.map((w, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className={`px-2 py-0.5 rounded text-xs font-medium mt-0.5 ${EFFORT_COLORS[w.effort] || 'bg-gray-100 text-gray-700'}`}>
                  {w.effort}
                </span>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{w.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{w.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estimates */}
      {(summary.solo_estimate || summary.team_estimate) && (
        <div className="p-5 border-b border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-3">
          {summary.solo_estimate && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Solo</p>
              <p className="text-base font-bold text-gray-900 dark:text-white">{summary.solo_estimate}</p>
            </div>
          )}
          {summary.team_estimate && (
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Small team / AI assist</p>
              <p className="text-base font-bold text-gray-900 dark:text-white">{summary.team_estimate}</p>
            </div>
          )}
        </div>
      )}

      {/* Architecture */}
      {summary.architecture_summary && (
        <div className="p-5 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Approach</h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{summary.architecture_summary}</p>
        </div>
      )}

      {/* Factory fit — soft tie-in, only when the model deemed it relevant */}
      {summary.factory_fit && (
        <div className="p-5 bg-primary-50 dark:bg-primary-900/20">
          <h3 className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wider mb-1">
            How Factory Nexus could help
          </h3>
          <p className="text-sm text-primary-800 dark:text-primary-200 leading-relaxed">{summary.factory_fit}</p>
          <Link
            // Signed-in users go straight to the builder (/ redirects them to the
            // dashboard and dropped the prompt); visitors get it prefilled on /.
            to={isAuthenticated ? '/build' : `/?prompt=${encodeURIComponent(buildPrompt)}`}
            state={isAuthenticated ? { prompt: buildPrompt } : undefined}
            onClick={onBuildClick}
            className="inline-block mt-3 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-500 transition-colors"
          >
            Build this with Factory
          </Link>
        </div>
      )}
    </div>
  )
}

function StackRow({ label, items, color }: { label: string; items: string[]; color: 'blue' | 'green' | 'purple' }) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  }
  return (
    <div>
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((t, i) => (
          <span key={i} className={`px-2 py-0.5 rounded text-sm ${colors[color]}`}>
            {t}
          </span>
        ))}
      </div>
    </div>
  )
}
